<template>
  <div class="modal-overlay">
    <div class="modal-background" @click="$emit('closeModal')" />
    <div class="modal">
      <div class="header">
        <div class="title-header">
          <span class="title">Type of tickets sold by day</span>
          <v-icon style="cursor: pointer; user-select: none" @click="$emit('closeModal')">
            close
          </v-icon>
        </div>

        <div class="radio-buttons-container">
          <div class="radio-buttons-title">
            Select a type of tickets you would like to generate a statistic for:
          </div>
          <div class="report-radio-buttons">
            <custom-radio-button
              label="All"
              :value="
                selectedTicketTypes.regular &&
                selectedTicketTypes.promo &&
                selectedTicketTypes.gratis
              "
              @click="handleRadioButtonClick(1)"
            />
            <custom-radio-button
              label="Regular + Promo"
              :value="
                selectedTicketTypes.regular &&
                selectedTicketTypes.promo &&
                !selectedTicketTypes.gratis
              "
              @click="handleRadioButtonClick(2)"
            />
            <custom-radio-button
              label="Promo"
              :value="
                !selectedTicketTypes.regular &&
                selectedTicketTypes.promo &&
                !selectedTicketTypes.gratis
              "
              @click="handleRadioButtonClick(3)"
            />
            <custom-radio-button
              label="Gratis"
              :value="
                !selectedTicketTypes.regular &&
                !selectedTicketTypes.promo &&
                selectedTicketTypes.gratis
              "
              @click="handleRadioButtonClick(4)"
            />
          </div>
        </div>
      </div>

      <div class="graph-container">
        <div v-if="!chartData" class="no-data-title">No data for selected period.</div>
        <pie-chart v-else :data="chartData" :options="{ displayLegend: true }" />
      </div>

      <div class="date-footer">
        <img
          src="/assets/chevron-left.svg"
          alt="left arrow"
          class="date-footer-arrow"
          @click="changeChartData(-1)"
        />
        <span class="selected-date">{{ formattedDate }}</span>
        <img
          src="/assets/chevron-right.svg"
          alt="left arrow"
          class="date-footer-arrow"
          @click="changeChartData(1)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import customRadioButton from '@/components/events/custom-radio-button.vue'
import PieChart from '@/components/shared/graphs/pie-chart.vue'
import moment from 'moment'
import { chartBackgroundColors } from '@/utils'

export default {
  name: 'ticket-types-statistics-modal',
  components: { PieChart, customRadioButton },
  props: {
    data: Array,
    initialSelectedDate: String,
  },
  data() {
    const selectedDateItem = this.data.find(item => item.date === this.initialSelectedDate) || {
      totalSold: 0,
      ticketsPromo: 0,
      ticketsGratis: 0,
      promoCodes: [],
    }

    return {
      selectedTicketTypes: {
        regular: true,
        promo: true,
        gratis: true,
      },
      selectedDate: this.initialSelectedDate,
      chartData: {
        labels: [
          'Regular',
          ...selectedDateItem.promoCodes.map(promoCode => promoCode.name),
          'Gratis',
        ],
        datasets: [
          {
            label: 'Vrste ulaznica',
            data: [
              selectedDateItem.totalSold -
                selectedDateItem.ticketsPromo -
                selectedDateItem.ticketsGratis,
              ...selectedDateItem.promoCodes.map(promoCode => promoCode.count),
              selectedDateItem.ticketsGratis,
            ],
            backgroundColor: chartBackgroundColors,
            hoverBackgroundColor: chartBackgroundColors,
          },
        ],
      },
    }
  },
  computed: {
    formattedDate() {
      return moment(this.selectedDate).format('DD/MM/YYYY')
    },
  },
  mounted() {
    this.changeChartData(0)
  },
  methods: {
    changeChartData(dateIndexChange) {
      const i = this.data.findIndex(item => item.date === this.selectedDate)
      if ((dateIndexChange > 0 && i >= this.data.length - 1) || (dateIndexChange < 0 && i <= 0)) {
        return
      }

      this.selectedDate = this.data[i + dateIndexChange].date
      this.chartData = {
        labels: [
          ...(this.selectedTicketTypes.regular ? ['Regular'] : []),
          ...(this.selectedTicketTypes.promo
            ? this.data[i + dateIndexChange].promoCodes.map(promoCode => promoCode.name)
            : []),
          ...(this.selectedTicketTypes.gratis ? ['Gratis'] : []),
        ],
        datasets: [
          {
            label: 'Vrste ulaznica',
            data: [
              ...(this.selectedTicketTypes.regular
                ? [
                    this.data[i + dateIndexChange].totalSold -
                      this.data[i + dateIndexChange].ticketsPromo -
                      this.data[i + dateIndexChange].ticketsGratis,
                  ]
                : []),
              ...(this.selectedTicketTypes.promo
                ? this.data[i + dateIndexChange].promoCodes.map(promoCode => promoCode.count)
                : []),
              ...(this.selectedTicketTypes.gratis
                ? [this.data[i + dateIndexChange].ticketsGratis]
                : []),
            ],
            backgroundColor: chartBackgroundColors,
            hoverBackgroundColor: chartBackgroundColors,
          },
        ],
      }
    },
    handleRadioButtonClick(option) {
      const optionsMap = {
        1: { regular: true, promo: true, gratis: true }, // All
        2: { regular: true, promo: true, gratis: false }, // Regular + Promo
        3: { regular: false, promo: true, gratis: false }, // Promo only
        4: { regular: false, promo: false, gratis: true }, // Gratis only
      }

      this.selectedTicketTypes = optionsMap[option] || this.selectedTicketTypes
      this.changeChartData(0)
    },
  },
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
}
.modal-background {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: #00000050;
}
.modal {
  text-align: left;
  background-color: white;
  position: relative;
  padding: 2rem;
  border-radius: 20px;
  overflow-y: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;

  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2.5rem;
  min-width: 50%;
}
.modal::-webkit-scrollbar {
  display: none;
}
@media screen and (max-width: 700px) {
  .modal {
    width: 100%;
    max-width: 100%;
    height: 100%;
    border-radius: 0;
  }
}
.header {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
  gap: 1rem;
  align-self: stretch;
}
.title-header {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  align-self: stretch;
}
.title {
  color: #0277bd;
  font-variant-numeric: lining-nums proportional-nums;

  font-family: Raleway;
  font-size: 1.25rem;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: -0.0375rem;
  flex: 1;
}
.radio-buttons-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.75rem;
  align-self: stretch;
}
.radio-buttons-title {
  font-family: 'Source Sans Pro', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 16.8px;
  color: #979797;
}
.graph-container {
  padding: 0;
}
.no-data-title {
  font-family: 'Source Sans Pro', sans-serif;
  font-style: normal;
  height: 100%;
  font-weight: 400;
  font-size: 14px;
  line-height: 16.8px;
  color: #979797;
  margin-bottom: 25px;
}
.date-footer {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  align-self: stretch;
  color: #666666;
}
.date-footer-arrow {
  width: 1.5rem;
  height: 1.5rem;
}
.date-footer-arrow:hover {
  cursor: pointer;
}
.selected-date {
  color: #666;
  font-variant-numeric: lining-nums proportional-nums;

  font-family: Raleway;
  font-size: 1.25rem;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: -0.0375rem;
}
</style>
